/**
 * 服务器地址解析。
 *
 * 网页版与桌面版共用同一套前端，但它们的「同源」含义完全不同：
 *
 *   - 网页版：页面本身就是服务端发的，用相对路径即可。
 *   - 桌面版：页面来自 `tauri://localhost` 的内嵌资源，
 *     相对路径会指向应用自身而不是服务器 —— 必须显式配置服务器地址。
 *
 * 这一点若不处理，桌面端能编译、能启动、界面也正常，
 * 但点登录永远失败，因为请求根本没发到服务器上。
 */

import { isDesktop } from './desktop'
import { currentServer } from './servers'

/**
 * 开发/测试用的覆盖地址。正常用户不填服务器地址 —— 桌面端与手机 App 走内置线路表
 * （见 servers.ts）；这个键只给本地联调和 e2e 用，界面上没有入口。
 */
// 键名带 dev：旧版桌面端把「服务器地址」输入框的值存在 whatylive.serverBase 里（默认 127.0.0.1:18131），
// 换键名等于让旧值自然失效 —— 否则升级后的应用会继续连本机，而线路表根本不生效。
const STORAGE_KEY = 'whatylive.devServerBase'

/** 规范化用户输入的服务器地址。 */
export function normalizeBase(input: string): string {
  let s = input.trim()
  if (!s) return ''
  // 允许用户只填 host:port，自动补协议。
  //
  // 没写协议时补 https 而不是 http：服务端必须走 HTTPS 音视频才能用
  // （浏览器只在安全上下文下开放摄像头麦克风），所以生产环境一定是
  // https。补成 http 的话，用户填对了地址却连不上，而错误只会说
  // 「无法连接」，看不出是协议选错了。
  //
  // 例外是明确的本机地址：开发时用 http://localhost 是常态，
  // 而 localhost 本来就是安全上下文，不需要证书。
  if (!/^https?:\/\//i.test(s)) {
    const isLocal = /^(localhost|127\.0\.0\.1|\[::1\])(:|$)/i.test(s)
    s = `${isLocal ? 'http' : 'https'}://${s}`
  }
  // 去掉结尾斜杠，避免拼出 //api/login。
  return s.replace(/\/+$/, '')
}

/** 当前服务器地址。网页版恒为空串（用同源）；桌面端/App 取覆盖地址，否则取当前线路。 */
export function getServerBase(): string {
  if (!isDesktop()) return ''
  try {
    const override = normalizeBase(localStorage.getItem(STORAGE_KEY) ?? '')
    if (override) return override
  } catch {
    /* 读不到就走线路表 */
  }
  return currentServer().base
}

/** 设置覆盖地址（本地联调 / e2e）。传空串清除，回到线路表。 */
export function setServerBase(base: string): void {
  const v = normalizeBase(base)
  try {
    if (v) localStorage.setItem(STORAGE_KEY, v)
    else localStorage.removeItem(STORAGE_KEY)
  } catch {
    // 隐私模式下写不进去也不影响本次会话。
  }
}

/** 拼出 API 地址。传入以 / 开头的路径。 */
export function apiURL(path: string): string {
  return getServerBase() + path
}

/**
 * 拼出附件/录像等静态资源地址。
 *
 * 服务端返回的是 `/blob/...`、`/record/...` 这类相对路径，
 * 桌面端必须补上服务器前缀，否则图片和录像都加载不出来。
 */
export function assetURL(path: string): string {
  if (!path) return path
  // 服务端将来若返回绝对地址，原样放行。
  if (/^https?:\/\//i.test(path) || path.startsWith('blob:') || path.startsWith('data:')) {
    return path
  }
  return getServerBase() + path
}

/** 拼出 WebSocket 地址。 */
export function wsURL(): string {
  const base = getServerBase()
  if (base) {
    return base.replace(/^http/i, 'ws') + '/ws'
  }
  const proto = location.protocol === 'https:' ? 'wss:' : 'ws:'
  return `${proto}//${location.host}/ws`
}
